import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Heart, ArrowRight, Download, Loader2, XCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/auth";

const MAX_POLLS = 15;
const POLL_INTERVAL = 2000;

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "polling", data: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus({ state: "error", data: null, error: "Missing payment session." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", data, error: null });
          return;
        }
        if (data.status === "expired" || data.status === "canceled") {
          setStatus({ state: "failed", data, error: "Payment was not completed." });
          return;
        }
        if (attemptsRef.current >= MAX_POLLS) {
          setStatus({ state: "timeout", data, error: "Still processing — check your email for confirmation." });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setStatus({ state: "error", data: null, error: "Unable to verify payment. Please contact support." });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const printReceipt = () => window.print();

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <main className="pt-28 pb-16">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-12 py-10 lg:py-16">
          {status.state === "polling" && (
            <div className="text-center py-20">
              <Loader2 className="w-12 h-12 text-[#23803F] mx-auto animate-spin mb-6" />
              <h1 className="text-3xl font-extrabold text-[#0B132B] mb-2">Confirming your donation…</h1>
              <p className="text-[#4B5563]">Please don't close this window.</p>
            </div>
          )}

          {status.state === "paid" && status.data && (
            <div>
              <div className="text-center mb-10">
                <div className="w-20 h-20 rounded-full bg-[#2D9B4E]/10 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-10 h-10 text-[#23803F]" strokeWidth={2.2} />
                </div>
                <p className="btc-eyebrow mb-3">DONATION CONFIRMED</p>
                <h1 className="text-4xl md:text-5xl font-extrabold text-[#0B132B] tracking-tight leading-tight mb-4">
                  Thank you, {status.data.donor_name?.split(" ")[0] || "friend"}.
                </h1>
                <p className="text-lg text-[#4B5563] max-w-xl mx-auto">
                  Your gift helps someone facing cancer feel less alone today. From all of us at
                  Bonded Thru Cancer — <span className="font-semibold text-[#0B132B]">thank you.</span>
                </p>
              </div>

              {/* Receipt card */}
              <div
                data-testid="donate-receipt"
                className="bg-white border border-gray-200 rounded-3xl p-8 lg:p-10 shadow-sm receipt-print"
              >
                <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">
                      Bonded Thru Cancer
                    </div>
                    <div className="text-sm text-[#0B132B]">Registered Charity No. {status.data.charity_number}</div>
                    <div className="text-sm text-[#0B132B]">United Kingdom Headquarters · Serving Patients Worldwide</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">Receipt</div>
                    <div className="font-mono text-sm text-[#0B132B]">{status.data.reference}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 py-6 border-y border-gray-100">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold mb-1">Donor</div>
                    <div className="text-[#0B132B] font-semibold">{status.data.donor_name}</div>
                    <div className="text-sm text-[#4B5563]">{status.data.donor_email}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold mb-1">Date</div>
                    <div className="text-[#0B132B] font-semibold">
                      {new Date(status.data.completed_at || status.data.created_at).toLocaleString("en-GB")}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold mb-1">Amount</div>
                    <div className="text-3xl font-extrabold text-[#23803F]">
                      £{Number(status.data.amount).toFixed(2)} {status.data.currency}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold mb-1">Gift Aid</div>
                    <div className="text-[#0B132B] font-semibold">
                      {status.data.gift_aid_declared ? "Declared" : "Not declared"}
                    </div>
                    {status.data.gift_aid_declared && (
                      <div className="text-sm text-[#4B5563]">
                        +£{(Number(status.data.amount) * 0.25).toFixed(2)} claimable
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex items-start gap-3 p-4 bg-[#F8F9FB] rounded-xl">
                  <Heart className="w-5 h-5 text-[#23803F] mt-0.5 shrink-0" fill="#2D9B4E" />
                  <p className="text-sm text-[#0B132B] leading-relaxed">
                    Your generosity funds specialist support, patient navigation, carer respite, and
                    the technology powering the BTC Patient Portal Ecosystem. Because of you, no one
                    faces cancer alone.
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap gap-3 no-print">
                  <button
                    onClick={printReceipt}
                    data-testid="donate-print-receipt"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-gray-200 text-[#0B132B] font-semibold text-sm hover:border-[#0B132B] transition"
                  >
                    <Download className="w-4 h-4" /> Print / Save receipt
                  </button>
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1A5FAB] text-white font-semibold text-sm hover:bg-[#154E8C] transition"
                  >
                    Create your BTC account <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[#0B132B] font-semibold text-sm hover:bg-[#F8F9FB] transition"
                  >
                    Back to home
                  </Link>
                </div>
              </div>
            </div>
          )}

          {(status.state === "failed" || status.state === "error" || status.state === "timeout") && (
            <div className="text-center py-16">
              {status.state === "timeout" ? (
                <Loader2 className="w-14 h-14 text-[#1A5FAB] mx-auto mb-5" />
              ) : (
                <XCircle className="w-14 h-14 text-[#B91C1C] mx-auto mb-5" />
              )}
              <h1 className="text-3xl font-extrabold text-[#0B132B] mb-3">
                {status.state === "timeout" ? "Still processing…" : "Payment not completed"}
              </h1>
              <p className="text-[#4B5563] max-w-md mx-auto mb-8">
                {status.error || "Your card was not charged."} If you were charged, please contact us at{" "}
                <a className="text-[#1A5FAB] font-semibold" href="mailto:support@bondedthrucancer.org">
                  support@bondedthrucancer.org
                </a>
                .
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                <Link to="/donate" className="btc-btn-primary">Try again <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/" className="btc-btn-outline">Back to home</Link>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <style>{`
        @media print {
          .no-print, header, footer { display: none !important; }
          body { background: #fff !important; }
          .receipt-print { border: 0 !important; box-shadow: none !important; padding: 0 !important; }
        }
      `}</style>
    </div>
  );
}
